import { useEffect, useRef, useState } from 'react';
import { cpfValido, somenteDigitos } from '../servicos/documento';
import { requisicaoAutenticada } from '../servicos/http';

/**
 * "Esta pessoa já tem cadastro?" — respondida enquanto se digita o CPF.
 *
 * ## Por que sozinha, sem botão
 *
 * O formulário de colaborador já dizia "se a pessoa já existir, o cadastro é
 * reaproveitado" — mas quem digitava o CPF de alguém cadastrado continuava
 * preenchendo nome, e-mail e telefone à mão, e um deles saía diferente do que
 * estava no banco. É a mesma escolha de `useConsultaDeCnpj`: um documento
 * válido consulta uma vez; mudar o número consulta de novo.
 *
 * ## O que ela não faz
 *
 * Não preenche nada por conta própria. Devolve a pessoa encontrada, e quem
 * usa decide o que aproveitar — no cadastro de colaborador, o que ainda está
 * em branco, para não apagar o que o operador acabou de escrever.
 */

export interface PessoaEncontrada {
  readonly personId: string;
  readonly nome: string;
  readonly emailAddress: string;
  readonly cpf: string | null;
  readonly telefone: string | null;
  readonly ativo: boolean;
  readonly temAcesso: boolean;
  readonly vinculos: readonly { readonly businessId: string; readonly razaoSocial: string }[];
}

export interface ConsultaDeCpf {
  readonly consultando: boolean;
  /** A pessoa cadastrada com este CPF, quando existe. */
  readonly pessoa: PessoaEncontrada | null;
  /** Some o aviso quando o operador já leu — sem apagar o que foi preenchido. */
  readonly dispensar: () => void;
}

export function useConsultaDeCpf(cpf: string, ativa = true): ConsultaDeCpf {
  const [consultando, setConsultando] = useState(false);
  const [pessoa, setPessoa] = useState<PessoaEncontrada | null>(null);
  const consultado = useRef<string | null>(null);

  const digitos = somenteDigitos(cpf);
  const valido = cpfValido(digitos);

  useEffect(() => {
    if (!ativa || !valido || consultado.current === digitos) return;

    let cancelado = false;
    consultado.current = digitos;
    setConsultando(true);

    void (async () => {
      try {
        const encontrada = await requisicaoAutenticada<PessoaEncontrada>(
          `/business/pessoas/cpf/${digitos}`,
        );
        if (!cancelado) setPessoa(encontrada);
      } catch {
        // 404 é a resposta normal para CPF novo — e 403 para quem não é admin.
        // Nos dois casos o formulário segue em branco, como antes.
        if (!cancelado) setPessoa(null);
      } finally {
        if (!cancelado) setConsultando(false);
      }
    })();

    return () => {
      cancelado = true;
      // O efeito duplo do StrictMode não pode deixar a consulta travada em
      // "consultando" nem marcar como consultado o que foi cancelado.
      if (consultado.current === digitos) consultado.current = null;
      setConsultando(false);
    };
  }, [digitos, valido, ativa]);

  // Trocar para um CPF incompleto limpa o achado: o aviso era da pessoa antiga.
  useEffect(() => {
    if (!valido) setPessoa(null);
  }, [valido]);

  return { consultando, pessoa, dispensar: () => setPessoa(null) };
}
